import { useEffect } from 'react';

interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

const SITE_NAME = 'TeknoNovaHub';
const SITE_URL = 'https://teknonovahub.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  'TeknoNovaHub - Türkiye\'nin en kapsamlı teknoloji haber sitesi. Yapay zeka, yazılım geliştirme, girişimcilik ve siber güvenlik haberleri.';

function setMeta(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(schemas: Record<string, unknown>[]) {
  // Remove old dynamic schemas
  document.querySelectorAll('script[data-dynamic-schema]').forEach((s) => s.remove());
  schemas.forEach((schema) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic-schema', 'true');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export function useSEO(config: SEOConfig) {
  useEffect(() => {
    if (!config.title) return;

    const fullTitle = config.title.includes(SITE_NAME)
      ? config.title
      : `${config.title} | ${SITE_NAME}`;
    const description = config.description || DEFAULT_DESCRIPTION;
    const image = config.image || DEFAULT_IMAGE;
    const url = config.url ? `${SITE_URL}${config.url}` : SITE_URL;
    const type = config.type || 'website';

    // Basic
    document.title = fullTitle;
    setMeta('description', description);
    if (config.keywords) setMeta('keywords', config.keywords);
    if (config.noIndex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow');
    }

    // Canonical
    setLink('canonical', url);

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:image', image, true);
    setMeta('og:url', url, true);
    setMeta('og:type', type, true);
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:locale', 'tr_TR', true);

    if (type === 'article' && config.publishedTime) {
      setMeta('article:published_time', config.publishedTime, true);
    }
    if (type === 'article' && config.modifiedTime) {
      setMeta('article:modified_time', config.modifiedTime, true);
    }
    if (type === 'article' && config.author) {
      setMeta('article:author', config.author, true);
    }

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    setMeta('twitter:site', '@teknonovahub');

    // last-modified
    const lm = document.querySelector('meta[http-equiv="last-modified"]') as HTMLMetaElement | null;
    if (lm) lm.content = new Date().toISOString().split('T')[0];

    // Schema
    if (config.schema) {
      const schemas = Array.isArray(config.schema) ? config.schema : [config.schema];
      setJsonLd(schemas);
    } else {
      document.querySelectorAll('script[data-dynamic-schema]').forEach((s) => s.remove());
    }

    return () => {
      // Reset to defaults on unmount
      document.title = `${SITE_NAME} | Türkiye'nin Teknoloji Haber Merkezi - Yapay Zeka, Yazılım, Girişimcilik`;
      document.querySelectorAll('script[data-dynamic-schema]').forEach((s) => s.remove());
    };
  }, [
    config.title,
    config.description,
    config.keywords,
    config.image,
    config.url,
    config.type,
    config.publishedTime,
    config.modifiedTime,
    config.author,
    config.noIndex,
  ]);
}

export default useSEO;
